import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { createAssertion, withdrawAssertion } from '@peruselab/api-client/assertions';
import { DiscussionError, type Reference } from '@peruselab/api-client/discussions';
import type { PublishedStatement } from '@peruselab/api-client/molecules';
import { Icon } from '../../../components/Icon';
import { ReferencePicker } from '../../discussions/ReferencePicker';
import { usePublicAccess } from '../../public/publicAccess';
import { FactsSection } from './FactsSection';
import { StatementEditing } from './statementEditing';

/** #947 (S3): the most a statement may say. */
export const MAX_STATEMENT = 4000;

export type StatementPropertyId = 'description' | 'use' | 'toxicity-summary';

/**
 * #947 phase 2 (S6): a person's statement -- a description, a use or a toxicity note -- in plain
 * text with its language, under the same evidence rule as a value (D8: a reference, or your own
 * unpublished result), public or private (D12). Correcting one of your own supersedes it (D13).
 */
export function StatementForm({
  moleculeId,
  propertyId,
  correcting,
  onDone,
  onCancel
}: {
  readonly moleculeId: string;
  readonly propertyId: StatementPropertyId;
  readonly correcting?: PublishedStatement | null;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const formId = useId();
  const [text, setText] = useState(correcting?.text ?? '');
  const [language, setLanguage] = useState(correcting?.language ?? i18n.language.split('-')[0] ?? 'en');
  const [references, setReferences] = useState<readonly Reference[]>([]);
  const [unpublished, setUnpublished] = useState(false);
  const [note, setNote] = useState('');
  const [audience, setAudience] = useState<'PUBLIC' | 'PRIVATE'>(correcting?.audience ?? 'PUBLIC');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasEvidence = references.length > 0 || unpublished;
  const ready = text.trim().length > 0 && hasEvidence && !busy;

  return (
    <form
      className="add-value-form statement-form"
      aria-labelledby={`${formId}-title`}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setBusy(true);
        setError(null);
        createAssertion(moleculeId, {
          propertyId,
          text: { text: text.trim(), language: language.trim() || 'en' },
          conditions: [],
          method: { kind: 'REPORTED', detail: null, confidence: null },
          references: references.map((reference) => reference.id),
          unpublished,
          unpublishedNote: unpublished ? note.trim() || null : null,
          audience,
          supersedes: correcting?.assertionId ?? null
        })
          .then(onDone)
          .catch((failure: unknown) => {
            setError(
              failure instanceof DiscussionError && failure.kind === 'invalid'
                ? failure.message
                : t(`discussions.errors.${failure instanceof DiscussionError ? failure.kind : 'unknown'}`)
            );
          })
          .finally(() => setBusy(false));
      }}
    >
      <h3 id={`${formId}-title`}>
        {correcting ? t('statements.correctTitle') : t(`statements.addTitle.${propertyId}`)}
      </h3>
      <label className="add-value-field">
        <span>{t('statements.text')}</span>
        <textarea
          dir="auto"
          lang={language || undefined}
          rows={4}
          maxLength={MAX_STATEMENT}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <span className="comment-meta" aria-live="polite">
          {t('statements.remaining', { count: MAX_STATEMENT - text.length })}
        </span>
      </label>
      <label className="add-value-field statement-form-language">
        <span>{t('statements.language')}</span>
        <input
          dir="ltr"
          maxLength={35}
          value={language}
          onChange={(event) => setLanguage(event.target.value)}
          placeholder="en"
        />
      </label>

      <fieldset className="add-value-evidence">
        <legend>{t('properties.form.evidence')}</legend>
        <ReferencePicker references={references} onChange={setReferences} disabled={busy} />
        <label className="add-value-check">
          <input type="checkbox" checked={unpublished} onChange={(event) => setUnpublished(event.target.checked)} />
          <span>{t('properties.form.unpublished')}</span>
        </label>
        {unpublished ? (
          <input
            aria-label={t('properties.form.unpublishedNote')}
            placeholder={t('properties.form.unpublishedNote')}
            dir="auto"
            maxLength={500}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        ) : null}
        {!hasEvidence ? <p className="discussion-note">{t('properties.form.evidenceNeeded')}</p> : null}
      </fieldset>

      <fieldset className="add-value-audience">
        <legend>{t('properties.form.audience')}</legend>
        {(['PUBLIC', 'PRIVATE'] as const).map((choice) => (
          <label key={choice} className="add-value-check">
            <input
              type="radio"
              name={`${formId}-audience`}
              checked={audience === choice}
              onChange={() => setAudience(choice)}
            />
            <span>{t(`properties.audience.${choice}`)}</span>
          </label>
        ))}
      </fieldset>

      {error ? (
        <p className="import-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="comment-composer-footer">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="icon-button"
          aria-label={t('discussions.cancel')}
          title={t('discussions.cancel')}
        >
          <Icon name="close" />
        </button>
        <button
          type="submit"
          className="icon-button"
          disabled={!ready}
          aria-label={correcting ? t('properties.form.saveCorrection') : t('statements.add')}
          title={correcting ? t('properties.form.saveCorrection') : t('statements.add')}
        >
          <Icon name="check" />
        </button>
      </div>
    </form>
  );
}

/**
 * #947 phase 2: "Add a use" (and the like) under a statement section -- the form for a signed-in
 * reader, "Sign in to add" otherwise; also the form for correcting one of your own.
 */
export function StatementAdder({
  moleculeId,
  propertyId,
  correcting,
  onCorrectDone,
  onChanged
}: {
  readonly moleculeId: string;
  readonly propertyId: StatementPropertyId;
  /** One of your own statements being corrected (from its Correct button). */
  readonly correcting?: PublishedStatement | null;
  readonly onCorrectDone?: () => void;
  /** Statements changed: load them again. */
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { signedIn, signIn } = usePublicAccess();
  const location = useLocation();
  const [adding, setAdding] = useState(false);
  if (adding || correcting) {
    return (
      <StatementForm
        moleculeId={moleculeId}
        propertyId={propertyId}
        correcting={correcting ?? null}
        onDone={() => {
          setAdding(false);
          onCorrectDone?.();
          onChanged();
        }}
        onCancel={() => {
          setAdding(false);
          onCorrectDone?.();
        }}
      />
    );
  }
  return signedIn ? (
    <button
      type="button"
      className="discussion-start icon-button statement-add"
      onClick={() => setAdding(true)}
      aria-label={t(`statements.addButton.${propertyId}`)}
      title={t(`statements.addButton.${propertyId}`)}
    >
      <Icon name="add" />
    </button>
  ) : (
    <button
      type="button"
      className="discussion-start icon-button statement-add"
      onClick={() => signIn(location.pathname + location.search)}
      aria-label={t('statements.signInToAdd')}
      title={t('statements.signInToAdd')}
    >
      <Icon name="lock" />
    </button>
  );
}

/**
 * #947 phase 2: a statement section -- its statements (sourced first, then people's), "Add a …"
 * below, and Correct / Withdraw on your own. With none yet, the heading and the add control alone.
 */
export function StatementSection({
  moleculeId,
  propertyId,
  title,
  statements,
  shownFirst,
  onChanged
}: {
  readonly moleculeId: string;
  readonly propertyId: StatementPropertyId;
  readonly title: string;
  readonly statements: readonly PublishedStatement[];
  readonly shownFirst?: number;
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const { editing, correcting, done } = useStatementEditing(onChanged);
  return (
    <StatementEditing.Provider value={editing}>
      {statements.length > 0 ? (
        <FactsSection title={title} statements={statements} {...(shownFirst ? { shownFirst } : {})} />
      ) : (
        <section className="published-facts-section" aria-labelledby={headingId}>
          <h2 id={headingId}>{title}</h2>
          <p className="discussion-note">{t(`statements.none.${propertyId}`)}</p>
        </section>
      )}
      <StatementAdder
        moleculeId={moleculeId}
        propertyId={propertyId}
        correcting={correcting}
        onCorrectDone={done}
        onChanged={onChanged}
      />
    </StatementEditing.Provider>
  );
}

/** #947 phase 2: Correct (opens the form on it) and Withdraw (asks, then marks it) for your own statements. */
export function useStatementEditing(onChanged: () => void) {
  const { t } = useTranslation();
  const [correcting, setCorrecting] = useState<PublishedStatement | null>(null);
  const editing = {
    onCorrect: (statement: PublishedStatement) => setCorrecting(statement),
    onWithdraw: (statement: PublishedStatement) => {
      if (!statement.assertionId || !globalThis.confirm(t('properties.confirmWithdraw'))) return;
      void withdrawAssertion(statement.assertionId).then(onChanged);
    }
  };
  return { editing, correcting, done: () => setCorrecting(null) };
}
